import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  X,
  Trash2,
  AlertTriangle,
  FolderTree,
  ListTodo,
  Users,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Project } from '@/types';
import { projectService, taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

interface DeleteProjectDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project;
}

type DeleteOption = 'delete_all' | 'orphan_tasks' | 'move_to_parent';

/**
 * DeleteProjectDialog
 * Confirmation dialog for project deletion with cascade options
 */
const DeleteProjectDialog: React.FC<DeleteProjectDialogProps> = ({
  open,
  onClose,
  project,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOption, setDeleteOption] = useState<DeleteOption>('delete_all');
  const [confirmationText, setConfirmationText] = useState('');

  // Fetch project tasks to show count
  const { data: tasksResponse } = useQuery({
    queryKey: QUERY_KEYS.projectTasks(project.id),
    queryFn: () => taskService.getTasksByProject(project.id),
    enabled: open,
  });

  // Fetch child projects
  const { data: childProjectsResponse } = useQuery({
    queryKey: [...QUERY_KEYS.projects, 'children', project.id],
    queryFn: () => projectService.getProjects(), // Filter for children
    enabled: open,
  });

  const tasks = tasksResponse ?? [];
  const allProjects = childProjectsResponse?.items ?? [];
  const childProjects = allProjects.filter(
    (p: Project) => p.parentId === project.id
  );

  // Calculate stats
  const stats = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter((t: any) => t.status === 'completed').length,
    inProgressTasks: tasks.filter((t: any) => t.status === 'in_progress').length,
    childProjectCount: childProjects.length,
    totalHoursLogged: tasks.reduce((sum: number, t: any) => sum + (t.actualMinutes || 0), 0),
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      // In a real app, handle delete options:
      // - delete_all: Delete project and all tasks/subprojects
      // - orphan_tasks: Delete project, keep tasks as unassigned
      // - move_to_parent: Delete project, move tasks to parent project
      
      // For now, we'll just delete the project
      return projectService.deleteProject(project.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      // Navigate to projects list
      navigate('/projects');
      onClose();
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const isConfirmationValid = confirmationText.trim().toLowerCase() === project.name.trim().toLowerCase();
  const hasSubprojects = stats.childProjectCount > 0;
  const hasTasks = stats.totalTasks > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'error.main',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Trash2 size={20} />
          <Typography variant="h6" fontWeight={600}>
            Delete Project
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Warning Alert */}
        <Alert
          severity="error"
          icon={<AlertTriangle size={20} />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            This action cannot be undone!
          </Typography>
          <Typography variant="body2">
            You are about to permanently delete the project "{project.name}".
          </Typography>
        </Alert>

        {/* Project Stats */}
        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            What will be affected:
          </Typography>
          <List dense disablePadding>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ListTodo size={18} color="#2563eb" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total Tasks</span>
                    <Typography component="span" fontWeight={600}>
                      {stats.totalTasks}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircle2 size={18} color="#16a34a" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Completed Tasks</span>
                    <Typography component="span" fontWeight={600}>
                      {stats.completedTasks}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Clock size={18} color="#ca8a04" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Hours Logged</span>
                    <Typography component="span" fontWeight={600}>
                      {stats.totalHoursLogged.toFixed(1)}h
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {hasSubprojects && (
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderTree size={18} color="#9333ea" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Child Projects</span>
                      <Typography component="span" fontWeight={600} color="error.main">
                        {stats.childProjectCount}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            )}
          </List>
        </Box>

        {/* Delete Options */}
        {hasTasks && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              How do you want to handle the tasks?
            </Typography>
            <RadioGroup
              value={deleteOption}
              onChange={(e) => setDeleteOption(e.target.value as DeleteOption)}
            >
              <FormControlLabel
                value="delete_all"
                control={<Radio color="error" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Delete everything
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Permanently delete all {stats.totalTasks} tasks and logged hours
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                value="orphan_tasks"
                control={<Radio color="error" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Keep tasks (unassigned)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tasks will become unassigned to any project
                    </Typography>
                  </Box>
                }
              />

              {project.parentId && (
                <FormControlLabel
                  value="move_to_parent"
                  control={<Radio color="error" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Move tasks to parent project
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tasks will be assigned to the parent project
                      </Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Subprojects Warning */}
        {hasSubprojects && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This project has {stats.childProjectCount} child project(s). They will become 
              orphaned (no parent) after deletion.
            </Typography>
          </Alert>
        )}

        {/* Confirmation Input */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To confirm deletion, type the project name below:
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              mb: 1,
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontFamily: 'monospace',
            }}
          >
            {project.name}
          </Typography>
          <TextField
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="Type project name to confirm"
            fullWidth
            size="small"
            error={confirmationText.length > 0 && !isConfirmationValid}
            helperText={
              confirmationText.length > 0 && !isConfirmationValid
                ? 'Project name does not match'
                : ''
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'error.main',
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!isConfirmationValid || deleteMutation.isPending}
          onClick={handleDelete}
          startIcon={
            deleteMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Trash2 size={18} />
            )
          }
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteProjectDialog;
